import { useEffect, useRef } from "react";

import {
  FORECAST_KEY,
  MY_FOCUS_FIELDS,
  ORG_FOCUS_FIELDS,
  ORG_GEO_FIELDS,
  ORG_GEO_SIMS,
  PROJECT_FOCUS_FIELDS,
  SHAPEFILE_FIELDS,
  SYNC_KEY
} from "constants/settings.constants";
import { getProductType } from "utils";

import { IGroupByListItem } from "models";

import { useGroupByStore } from "../GroupByStoreProvider";
import {
  selectAllColumns,
  selectCategoryList,
  selectSelectedCategory,
  selectSelectedField
} from "../groupBySelectors";
import type { PropsT } from "../groupByStore";

/**
 * Custom-field properties name their category in a prefix rather than a path
 * segment, so `getCategoryName` parsing can't resolve them. The three
 * `Org_Focus_Fields*` entries must stay most-specific-first. The trailing
 * underscore on the geo prefix is load-bearing: it keeps geom bins
 * (`Org_Focus_Fields_GeomBin_*`) out of the geo category.
 */
const CUSTOM_FIELD_CATEGORY_BY_PREFIX: ReadonlyArray<[string, string]> = [
  ["Org_Focus_Fields_Sim_Geo_", ORG_GEO_SIMS],
  ["Org_Focus_Fields_Geo_", ORG_GEO_FIELDS],
  ["Org_Focus_Fields", ORG_FOCUS_FIELDS],
  ["Org_Midstream_Fields", ORG_FOCUS_FIELDS],
  ["My_Focus_Fields", MY_FOCUS_FIELDS],
  ["My_Midstream_Fields", MY_FOCUS_FIELDS],
  ["Project_Focus_Fields", PROJECT_FOCUS_FIELDS]
];

/**
 * Uses the prop value to set selected-category and selected-product in context
 */
const useProps = (props: PropsT): void => {
  const { chartType, dataTypeFilters, editable, value } = props;
  const allColumns = useGroupByStore(selectAllColumns);
  const categoryList = useGroupByStore(selectCategoryList);
  const selectedCategory = useGroupByStore(selectSelectedCategory);
  const selectedField = useGroupByStore(selectSelectedField);
  const setProps = useGroupByStore((s) => s.setProps);
  const setForecastToggle = useGroupByStore((s) => s.setForecastToggle);
  const setSelectedCategory = useGroupByStore((s) => s.setSelectedCategory);
  const setSelectedField = useGroupByStore((s) => s.setSelectedField);
  const setSelectedProduct = useGroupByStore((s) => s.setSelectedProduct);
  const setSelectedStartDate = useGroupByStore((s) => s.setSelectedStartDate);
  const setSelectedEndDate = useGroupByStore((s) => s.setSelectedEndDate);
  const selectedCategoryNameRef = useRef<string>();
  const resolvedPropertyRef = useRef<string>();

  useEffect(() => {
    selectedCategoryNameRef.current = selectedCategory?.name;
    return () => {
      selectedCategoryNameRef.current = null;
    };
  }, [selectedCategory]);

  useEffect(() => {
    const isPlusForecast = value.property.endsWith(`.${FORECAST_KEY}`);
    if (!isPlusForecast) return;
    setForecastToggle(isPlusForecast);
  }, [setForecastToggle, value.property]);

  // Sync performanceDateRange from the parent value into the store so
  // DateRangeSelect shows the current date range when the popup opens.
  useEffect(() => {
    const pdr = (
      value as { performanceDateRange?: { startDate: string; endDate: string } }
    ).performanceDateRange;
    if (pdr?.startDate && pdr?.endDate) {
      const start = new Date(
        parseInt(pdr.startDate.slice(0, 4)),
        parseInt(pdr.startDate.slice(4, 6)) - 1,
        parseInt(pdr.startDate.slice(6, 8))
      );
      const end = new Date(
        parseInt(pdr.endDate.slice(0, 4)),
        parseInt(pdr.endDate.slice(4, 6)) - 1,
        parseInt(pdr.endDate.slice(6, 8))
      );
      setSelectedStartDate(start);
      setSelectedEndDate(end);
    } else {
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  }, [
    (value as { performanceDateRange?: { startDate: string; endDate: string } })
      .performanceDateRange?.startDate,
    (value as { performanceDateRange?: { startDate: string; endDate: string } })
      .performanceDateRange?.endDate,
    setSelectedStartDate,
    setSelectedEndDate
  ]);

  // save props to context — consolidated into a single effect covering all
  // parent-prop fields that should trigger a store sync.
  useEffect(() => {
    setProps(props);
  }, [
    setProps,
    props.chartTypeParam,
    props.pinnedPdenSource,
    editable,
    dataTypeFilters,
    chartType
  ]);

  // A picker with no chosen field opens on the caller's default category.
  useEffect(() => {
    if (!props.defaultCategoryName || value?.title || selectedCategory) return;
    const category = categoryList?.find(({ name }) => name === props.defaultCategoryName);
    if (category) setSelectedCategory(category);
  }, [categoryList, props.defaultCategoryName]);

  useEffect(() => {
    //when users updated the property this will update the correct selected
    //category ref
    const categoryName = value?.property ? getCategoryName(value.property) : "";
    if (selectedCategoryNameRef.current !== categoryName && categoryList) {
      const category = (categoryList ?? []).find(
        ({ name }) => name === categoryName || categoryName.startsWith(name)
      );

      if (category) {
        selectedCategoryNameRef.current = category?.name;
      }
    }
  }, [value]);

  function getCustomFieldCategory(property: string): Maybe<string> {
    return CUSTOM_FIELD_CATEGORY_BY_PREFIX.find(([prefix]) =>
      property.startsWith(prefix)
    )?.[1];
  }

  function getCategoryName(property: string): string {
    return (
      getCustomFieldCategory(property) ??
      property.split(".").find(Boolean).split("_").join(" ").replace("RandD", "R&D")
    );
  }

  useEffect(() => {
    if (!value?.title) return;
    if (!categoryList?.length) return;
    const { property } = value;
    if (!property) {
      return;
    }

    // update selected-field
    const field = allColumns.find((column) => column.property === property);

    // Only the column list was refreshed. Re-deriving the category from an
    // unchanged property would pull the user out of the category they opened.
    // Latched on a resolved category, so a property whose column arrives in a
    // later refresh still gets one.
    if (resolvedPropertyRef.current === property) {
      if (field && field !== selectedField) {
        setSelectedField(field);
      }
      return;
    }

    // The prefix wins over `group` for custom fields: org and personal custom
    // columns carry an empty `group`, and geom bins report "GeomBin", which is
    // a subgroup rather than a category. `group` is only consulted for built-in
    // columns, whose property path doesn't always name their category.
    const categoryName =
      getCustomFieldCategory(property) ?? (field?.group || getCategoryName(property));

    let category: IGroupByListItem;

    // This case to cover update (add/delete) items from Shapefile, My Fields and Organization Fields
    // We would refer to reload the Category List into the tab with updated data
    if (
      selectedCategoryNameRef.current === SHAPEFILE_FIELDS ||
      selectedCategoryNameRef.current === MY_FOCUS_FIELDS ||
      selectedCategoryNameRef.current === ORG_FOCUS_FIELDS ||
      selectedCategoryNameRef.current === ORG_GEO_FIELDS ||
      selectedCategoryNameRef.current === PROJECT_FOCUS_FIELDS ||
      selectedCategoryNameRef.current === ORG_GEO_SIMS
    ) {
      category = categoryList.find(
        ({ name }) => name === selectedCategoryNameRef.current
      );
      if (selectedCategoryNameRef.current !== categoryName) {
        // Deliberately not latched: the category selected here is the one the
        // user browsed to, not the property's own. Latching would make the
        // early return fire before the property's real category is ever
        // resolved. Re-running this branch on each refresh is what main does
        // and reaches the same end state.
        setSelectedCategory(category);
        if (field != selectedField) {
          setSelectedField(field);
        }
        return;
      }
    } else {
      category = categoryList.find(({ name }) => name === categoryName);
    }

    // TODO BF: some notes, overall this useEffect is confusing and has a lot of side effects
    // Consider investigating change, it is unclear why the selected category is being updated here, but it could have unexpected side effects
    // from how this category name is retrieved (by extracting the category from the selected property).
    // It seems like we're using the value property to 'override' the category in scenarios, may not be needed after refactor to column groups
    // If over riding is still neccessary, we could probably just put this in the column selection event
    // Unless this was initially covering application load where we need to set the selected category based on the initial property, if so we should document it
    if (category?.name) {
      //update when the categories are the same
      resolvedPropertyRef.current = property;
      setSelectedCategory(category);
    }

    // update selected-product, if category is "Performance" or "Forecast"
    const numberOfDots = property.split(".").length - 1;

    const hasProductNameInProperty =
      numberOfDots > 1 &&
      property.split(".")[0] !== "QC_Meta" &&
      !property.includes(`.${SYNC_KEY}.`);

    if (hasProductNameInProperty) {
      const productLabel = property.split(".")[1];
      const product = getProductType(productLabel);

      setSelectedProduct(product.label);
    }

    if (field) {
      setSelectedField(field);
    }
  }, [categoryList, value.property]);
};

export default useProps;
